"use client";

import type { FeaturesContent } from "@/components/landing/registry/content";
import type { PublicSectionProps } from "@/components/landing/registry/types";
import type { FontId, SectionId, TemplateId } from "@/components/landing/StoreCustomizer/types";
import { faPalette } from "@fortawesome/pro-solid-svg-icons";
import { useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import { pickBi } from "@/components/landing/registry/locale";
import {
	GlowBackground,
	MotionReveal,
	SectionShell,
	useSectionMotion,
} from "@/components/landing/shared";
import { BRAND_COLORS, TEMPLATE_VISUALS } from "@/components/landing/StoreCustomizer/config";
import ControlPanel from "@/components/landing/StoreCustomizer/ControlPanel";
import CornerAccents from "@/components/landing/StoreCustomizer/CornerAccents";
import {
	CUSTOMIZER_REDUCED_TRANSITION,
	CUSTOMIZER_SPRING,
} from "@/components/landing/StoreCustomizer/customizerMotion";
import InteractiveCallout from "@/components/landing/StoreCustomizer/InteractiveCallout";
import StoreCustomizerIntro from "@/components/landing/StoreCustomizer/StoreCustomizerIntro";
import StorePreview from "@/components/landing/StoreCustomizer/StorePreview";
import { Icon } from "@/components/ui/Icon";

// Studio's Features content schema only covers copy + the demo storefront
// mockup — this surrounding chrome (template/section/color picker labels)
// has no admin-editable source, so it's hardcoded here verbatim from the
// previous i18n strings, keyed off the page locale.
const CHROME = {
	en: {
		panelTitle: "Storefront design",
		templateLabel: "Template",
		templates: { fresh: "Fresh", bold: "Bold", classic: "Classic" } satisfies Record<
			TemplateId,
			string
		>,
		logoLabel: "Logo",
		uploadLabel: "Upload",
		removeLogoLabel: "Remove logo",
		colorLabel: "Brand color",
		fontLabel: "Font",
		fonts: {
			system: "System",
			cairo: "Cairo",
			tajawal: "Tajawal",
			rubik: "Rubik",
		} satisfies Record<FontId, string>,
		sectionsLabel: "Homepage sections",
		sections: {
			hero: "Hero banner",
			featured: "Featured products",
			categories: "Categories",
		} satisfies Record<SectionId, string>,
		livePreviewLabel: "Live preview",
		captionLabel: "Same store, both languages",
		calloutLabel: "Try the interactive preview",
	},
	ar: {
		panelTitle: "تصميم واجهة المتجر",
		templateLabel: "القالب",
		templates: { fresh: "منعش", bold: "جريء", classic: "كلاسيكي" } satisfies Record<
			TemplateId,
			string
		>,
		logoLabel: "الشعار",
		uploadLabel: "رفع",
		removeLogoLabel: "إزالة الشعار",
		colorLabel: "لون العلامة",
		fontLabel: "الخط",
		fonts: {
			system: "افتراضي",
			cairo: "Cairo",
			tajawal: "Tajawal",
			rubik: "Rubik",
		} satisfies Record<FontId, string>,
		sectionsLabel: "أقسام الصفحة الرئيسية",
		sections: {
			hero: "بانر الواجهة",
			featured: "المنتجات المميزة",
			categories: "التصنيفات",
		} satisfies Record<SectionId, string>,
		livePreviewLabel: "معاينة مباشرة",
		captionLabel: "نفس المتجر، بلغتين",
		calloutLabel: "جرّب المعاينة التفاعلية",
	},
} as const;

interface InnerProps {
	locale: string;
	eyebrow: string;
	title: string;
	subtitle: string;
	benefits: string[];
	content: FeaturesContent;
}

function Inner(prop: InnerProps) {
	const { locale, eyebrow, title, subtitle, benefits, content } = prop;
	const motion = useSectionMotion();
	const chrome = locale === "ar" ? CHROME.ar : CHROME.en;
	// Editable in the studio (Features content); falls back to the hardcoded
	// chrome string when the admin leaves it blank.
	const calloutLabel = pickBi(content.calloutLabel, locale) || chrome.calloutLabel;

	const [templateId, setTemplateId] = useState<TemplateId>("fresh");
	const [colorId, setColorId] = useState<(typeof BRAND_COLORS)[number]["id"]>(BRAND_COLORS[0].id);
	const [fontId, setFontId] = useState<FontId>("cairo");
	const [logoUrl, setLogoUrl] = useState<string | null>(null);
	const [visible, setVisible] = useState<Record<SectionId, boolean>>({
		hero: true,
		featured: true,
		categories: true,
	});

	// Revokes the previous blob: URL whenever it's replaced or the widget unmounts.
	useEffect(() => {
		return () => {
			if (logoUrl) URL.revokeObjectURL(logoUrl);
		};
	}, [logoUrl]);
	const [previewLang, setPreviewLang] = useState<"en" | "ar">(locale === "ar" ? "ar" : "en");
	const reduceMotion = useReducedMotion();
	const transition = reduceMotion ? CUSTOMIZER_REDUCED_TRANSITION : CUSTOMIZER_SPRING;

	const template = TEMPLATE_VISUALS[templateId];
	const accent = BRAND_COLORS.find((c) => c.id === colorId)?.hex ?? BRAND_COLORS[0].hex;

	function toggleSection(id: SectionId) {
		setVisible((prev) => ({ ...prev, [id]: !prev[id] }));
	}

	return (
		<>
			<GlowBackground
				className="-top-16 start-1/2 h-144 w-xl -translate-x-1/2 opacity-20 rtl:translate-x-1/2"
				style={{ background: "radial-gradient(circle, var(--brand-2), transparent 60%)" }}
			/>

			<div className="mx-auto grid max-w-6xl items-start gap-12 px-6 lg:grid-cols-[1fr_1.3fr] lg:items-center">
				<MotionReveal motion={motion}>
					<StoreCustomizerIntro
						eyebrow={eyebrow}
						title={title}
						subtitle={subtitle}
						benefits={benefits}
					/>
				</MotionReveal>

				<MotionReveal motion={motion} delayOffset={120} className="relative pt-44 lg:pt-0">
					{/* Desktop: annotation floats up-left of the mockup, arrow curves right into it. */}
					<InteractiveCallout
						direction="right"
						label={calloutLabel}
						className="inset-e-full -top-14 z-20 me-6 hidden lg:block"
					/>
					{/* Stacked: annotation sits above the mockup, arrow curves down into it. */}
					<InteractiveCallout
						direction="down"
						label={calloutLabel}
						className="inset-s-6 top-1 z-20 w-40 sm:inset-s-8 lg:hidden"
					/>
					{/* Zero-spacing wrapper so the accent marks anchor to the card's
              actual corners regardless of the mobile top padding above. */}
					<div className="relative">
						{/* Decorative hand-drawn emphasis marks outside the mockup's right corners. */}
						<CornerAccents
							variant="top"
							className="-inset-e-10 top-0 hidden w-9 -translate-y-1/2 xl:block 2xl:w-11"
						/>
						<CornerAccents
							variant="bottom"
							className="-inset-e-10 bottom-0 hidden w-9 translate-y-1/2 xl:block 2xl:w-11"
						/>
						<div className="overflow-hidden rounded-[1.25rem] border border-border bg-surface shadow-(--shadow)">
							<div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
								<span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[color-mix(in_srgb,var(--brand-2)_16%,transparent)] text-(--brand-2)">
									<Icon icon={faPalette} size={15} />
								</span>
								<h3 className="text-sm font-semibold">{chrome.panelTitle}</h3>
							</div>

							<div className="grid gap-0 sm:grid-cols-[1fr_1.2fr]">
								<ControlPanel
									templateLabel={chrome.templateLabel}
									templates={chrome.templates}
									templateId={templateId}
									onTemplateChange={setTemplateId}
									logoLabel={chrome.logoLabel}
									uploadLabel={chrome.uploadLabel}
									removeLogoLabel={chrome.removeLogoLabel}
									logoUrl={logoUrl}
									onLogoChange={setLogoUrl}
									colorLabel={chrome.colorLabel}
									colorId={colorId}
									onColorChange={setColorId}
									fontLabel={chrome.fontLabel}
									fonts={chrome.fonts}
									fontId={fontId}
									onFontChange={setFontId}
									sectionsLabel={chrome.sectionsLabel}
									sections={chrome.sections}
									visible={visible}
									accent={accent}
									onToggleSection={toggleSection}
								/>

								<StorePreview
									livePreviewLabel={chrome.livePreviewLabel}
									captionLabel={chrome.captionLabel}
									previewLang={previewLang}
									onLangChange={setPreviewLang}
									template={template}
									accent={accent}
									fontId={fontId}
									logoUrl={logoUrl}
									visible={visible}
									transition={transition}
									content={content}
								/>
							</div>
						</div>
					</div>
				</MotionReveal>
			</div>
		</>
	);
}

function StoreCustomizer(prop: PublicSectionProps<FeaturesContent>) {
	const { instance, locale } = prop;
	const { content, general, layout, style } = instance;

	return (
		<SectionShell
			general={general}
			layout={layout}
			style={style}
			motion={instance.motion}
			className="jana-bg-teal relative scroll-mt-20 overflow-hidden py-16 md:py-24"
		>
			<Inner
				locale={locale}
				eyebrow={pickBi(content.eyebrow, locale)}
				title={pickBi(content.title, locale)}
				subtitle={pickBi(content.subtitle, locale)}
				benefits={content.benefits.map((b) => pickBi(b, locale))}
				content={content}
			/>
		</SectionShell>
	);
}

export default StoreCustomizer;
